import { useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { GetTeacherInformation } from "../../actions/teacherQueries";
import { SaveHiddenClassIds } from "../../actions/teacherMutations";
import useChooseSidebarClassesStore from "../../stores/useChooseSidebarClassesStore";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { USER_TYPE_ICONS } from "../../Utilities/userTypeIcons";
import { compareByName } from "../../Utilities/sortHelper";

export default function ChooseSidebarClassesModal() {
  const intl = useIntl();
  const myClassLabel = intl.formatMessage({ id: "YouAreTeacherInClass" });
  const queryClient = useQueryClient();

  const isOpen = useChooseSidebarClassesStore((state) => state.isOpen);
  const programmeId = useChooseSidebarClassesStore(
    (state) => state.programmeId,
  );
  const close = useChooseSidebarClassesStore((state) => state.close);

  const [search, setSearch] = useState("");
  const [editedCheckedClassIds, setEditedCheckedClassIds] = useState(null);

  const teacherInfo = GetTeacherInformation();
  const saveHiddenClassesMutation = useMutation({
    mutationFn: SaveHiddenClassIds,
  });

  const programmes = teacherInfo.data?.programmes ?? [];
  const hiddenClassIds = teacherInfo.data?.hiddenClassIds ?? [];
  const programmeClasses =
    programmes.find((prog) => prog.id === programmeId)?.classes ?? [];
  const programmeClassIds = programmeClasses.map((cl) => cl.id);
  const isMyClass = (cl) =>
    cl.teachers?.some((teacher) => teacher.id === teacherInfo.data?.id);
  const longestClassLabelLength = Math.max(
    0,
    ...programmeClasses.map((cl) => cl.name.length + (isMyClass(cl) ? 3 : 0)),
  );
  const checkedClassIds =
    editedCheckedClassIds ??
    programmeClassIds.filter((id) => !hiddenClassIds.includes(id));

  const filteredClasses = programmeClasses
    .filter((cl) => cl.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort(compareByName);
  const filteredClassIds = filteredClasses.map((cl) => cl.id);
  const allFilteredChecked =
    filteredClassIds.length > 0 &&
    filteredClassIds.every((id) => checkedClassIds.includes(id));
  const anyFilteredChecked = filteredClassIds.some((id) =>
    checkedClassIds.includes(id),
  );

  const toggleClass = (classId) =>
    setEditedCheckedClassIds(
      checkedClassIds.includes(classId)
        ? checkedClassIds.filter((id) => id !== classId)
        : [...checkedClassIds, classId],
    );

  const toggleAllFiltered = () =>
    setEditedCheckedClassIds(
      allFilteredChecked
        ? checkedClassIds.filter((id) => !filteredClassIds.includes(id))
        : [
            ...checkedClassIds,
            ...filteredClassIds.filter((id) => !checkedClassIds.includes(id)),
          ],
    );

  const resetOnClose = () => {
    setSearch("");
    setEditedCheckedClassIds(null);
  };

  const save = () => {
    const hiddenInOtherProgrammes = hiddenClassIds.filter(
      (id) => !programmeClassIds.includes(id),
    );
    const hiddenInThisProgramme = programmeClassIds.filter(
      (id) => !checkedClassIds.includes(id),
    );
    saveHiddenClassesMutation.mutate(
      [...hiddenInOtherProgrammes, ...hiddenInThisProgramme],
      {
        onSuccess: (data) => {
          queryClient.setQueryData(["FetchTeacherInformation"], (teacher) =>
            teacher
              ? { ...teacher, hiddenClassIds: data.data.result }
              : teacher,
          );
          close();
        },
        onError: (err) => {
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      },
    );
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={close} onClosed={resetOnClose}>
      <ModalHeader toggle={close}>
        <FormattedMessage id="ChooseClassesInSidebar" />
      </ModalHeader>
      <ModalBody>
        <p className="mb-2">
          <FormattedMessage id="ChooseClassesInSidebarInfo" />
        </p>
        {programmeClasses.some(isMyClass) ? (
          <p className="text-muted mb-2">
            <FontAwesomeIcon icon={USER_TYPE_ICONS.Teacher} size="sm" /> ={" "}
            <FormattedMessage id="ClassesWhereYouAreTeacher" />
          </p>
        ) : (
          ""
        )}
        <Input
          type="search"
          value={search}
          placeholder={intl.formatMessage({ id: "SearchClass" })}
          onChange={(e) => setSearch(e.target.value)}
        />
        {teacherInfo.isError ? (
          <p className="text-danger mt-2 mb-0">
            <FormattedMessage id="CouldNotLoadData" />
          </p>
        ) : (
          ""
        )}
        <FormGroup check className="mt-2 mb-0">
          <Input
            id="sidebar-class-all"
            type="checkbox"
            checked={allFilteredChecked}
            innerRef={(el) => {
              if (el)
                el.indeterminate = anyFilteredChecked && !allFilteredChecked;
            }}
            disabled={!teacherInfo.isSuccess || filteredClassIds.length === 0}
            onChange={toggleAllFiltered}
          />
          <Label check for="sidebar-class-all">
            <FormattedMessage id="SelectAll" />
          </Label>
        </FormGroup>
        <hr className="my-2" />
        <div
          style={{
            maxHeight: "50vh",
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${longestClassLabelLength + 4}ch), 1fr))`,
            columnGap: "1rem",
          }}
        >
          {filteredClasses.map((cl) => (
            <FormGroup check key={cl.id}>
              <Input
                id={`sidebar-class-${cl.id}`}
                type="checkbox"
                checked={checkedClassIds.includes(cl.id)}
                disabled={!teacherInfo.isSuccess}
                onChange={() => toggleClass(cl.id)}
              />
              <Label check for={`sidebar-class-${cl.id}`}>
                {isMyClass(cl) ? (
                  <span
                    className="me-1"
                    role="img"
                    aria-label={myClassLabel}
                    title={myClassLabel}
                  >
                    <FontAwesomeIcon
                      icon={USER_TYPE_ICONS.Teacher}
                      size="sm"
                      className="teacher-in-class-icon"
                    />
                  </span>
                ) : (
                  ""
                )}
                {cl.name}
              </Label>
            </FormGroup>
          ))}
        </div>
        {filteredClasses.length === 0 ? (
          <p className="text-muted mb-0">
            <FormattedMessage id="NoMatches" />
          </p>
        ) : (
          ""
        )}
        <p className="text-muted mt-2 mb-0">
          <FormattedMessage
            id="ShownClassesCount"
            values={{
              shown: checkedClassIds.length,
              total: programmeClassIds.length,
            }}
          />
        </p>
      </ModalBody>
      <ModalFooter>
        <Button outline color="secondary" onClick={close}>
          <FormattedMessage id="Cancel" />
        </Button>
        <Button
          outline
          color="primary"
          disabled={
            editedCheckedClassIds === null ||
            !teacherInfo.isSuccess ||
            saveHiddenClassesMutation.isPending
          }
          onClick={save}
        >
          <FormattedMessage id="Save" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
